import type { PluginListenerHandle } from "@capacitor/core";
import { Capacitor } from "@capacitor/core";
import { ImpactStyle } from "@capacitor/haptics";
import { Keyboard } from "@capacitor/keyboard";
import { ChevronLeftIcon } from "@heroicons/react/outline";
import * as React from "react";
import { matchPath } from "react-router";
import { useLocation } from "react-router-dom";
import { hapticsImpact } from "~/config/capacitor/haptics/hook";
import { useUser } from "~/config/user/UserProvider";
import { Box } from "~/design-system/Box";
import { Button } from "~/design-system/Button";
import { Drawer, DrawerContent, DrawerTrigger } from "~/design-system/Drawer";
import { useBreakpoint } from "~/design-system/hooks";
import { Stack } from "~/design-system/Stack";
import { createComponentHook } from "~/types";
import { NavigationDrawerHeader } from "./components/NavigationDrawerHeader/NavigationDrawerHeader";
import { ProjectListMenu } from "./components/ProjectListMenu/ProjectListMenu";
import { SettingsDrawerButton } from "./components/SettingsDrawer/components/SettingsDrawerButton";
import { SettingsDrawer } from "./components/SettingsDrawer/SettingsDrawer";
import type {
  NavigationDrawer_OrgQuery,
  NavigationDrawer_UserQuery,
} from "./NavigationDrawer.graphql";
import {
  useNavigationDrawer_OrgQuery,
  useNavigationDrawer_UserQuery,
} from "./NavigationDrawer.graphql";

type UserInfos = NonNullable<NavigationDrawer_UserQuery["user"]>;
type UserOrg = NonNullable<UserInfos["active_org"]>["org"];
type UserProject = NonNullable<UserInfos["projects"]>[number]["project"];
type GroupProjectList = NonNullable<
  NavigationDrawer_OrgQuery["org"]
>["project_groups"];
type OrgSettings = NonNullable<NavigationDrawer_OrgQuery["org"]>["settings"];

const useNavigationDrawer = createComponentHook(() => {
  const { breakpoint } = useBreakpoint();
  const [open, setOpen] = React.useState(false);
  const [keyboardOpen, setKeyboardOpen] = React.useState(false);
  const location = useLocation();
  const matchedUrl = matchPath("/project/:projectId/*", location.pathname);
  let activeProjectId;
  if (matchedUrl) {
    const { projectId } = matchedUrl?.params as { projectId: string };
    activeProjectId = projectId;
  }
  const { user } = useUser();

  const currentUserOrgId = React.useMemo(
    () => user.active_org?.org.id,
    [user.active_org]
  );

  const queryContext = React.useMemo(
    () => ({
      additionalTypenames: ["orgs", "projects", "project_groups"],
    }),
    []
  );

  const [{ data: orgData }] = useNavigationDrawer_OrgQuery({
    context: queryContext,
    variables: { orgId: currentUserOrgId! },
  });

  const [{ data: userData, fetching: userFetching }, reexecuteUserQuery] =
    useNavigationDrawer_UserQuery({
      context: queryContext,
      variables: { id: user?.id },
    });

  const retryCountRef = React.useRef(0);
  const retryTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Clear timeout on unmount to avoid memory leaks
  React.useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (userData?.user) {
      retryCountRef.current = 0; // reset after success
      return;
    }

    if (!userFetching && !userData?.user && retryCountRef.current < 3) {
      retryCountRef.current += 1;

      const delayMs = 500 * retryCountRef.current; // exponential backoff
      retryTimeoutRef.current = setTimeout(() => {
        reexecuteUserQuery({ requestPolicy: "network-only" });
      }, delayMs);
    }
  }, [userData, userFetching, reexecuteUserQuery]);

  // Keep a stable reference to user data - never let it become null
  const userDataRef = React.useRef(userData?.user);

  React.useEffect(() => {
    if (userData?.user) {
      userDataRef.current = userData.user;
    }
  }, [userData?.user]);

  // Always use a valid user - either current or last known
  const userInfos = userData?.user ?? userDataRef.current;

  const projects = React.useMemo(
    () => userInfos?.projects?.map((p) => p.project) ?? [],
    [userInfos?.projects]
  );

  const org = React.useMemo(
    () => userInfos?.active_org?.org,
    [userInfos?.active_org]
  );

  const originalInviter = React.useMemo(
    () => userInfos?.originalInviter?.[0]?.inviter ?? undefined,
    [userInfos?.originalInviter]
  );

  /* Fetch the projects groups from the org, if the user has an org */
  const groupProjectList: GroupProjectList = React.useMemo(
    () => orgData?.org?.project_groups ?? [],
    [orgData?.org?.project_groups]
  );

  /* Fetch the org settings to see if projects should be goruped or not */
  const orgSettings: OrgSettings = React.useMemo(
    () => orgData?.org?.settings,
    [orgData?.org?.settings]
  );

  const handleHaptic = React.useCallback(async () => {
    await hapticsImpact(ImpactStyle.Light);
  }, []);

  React.useEffect(() => {
    let showListener: PluginListenerHandle;
    let hideListener: PluginListenerHandle;
    async function hideOnChatKeyboard() {
      if (Capacitor.isPluginAvailable("Keyboard")) {
        showListener = await Keyboard.addListener("keyboardWillShow", () => {
          setKeyboardOpen(true);
        });
        hideListener = await Keyboard.addListener("keyboardDidHide", () => {
          setKeyboardOpen(false);
        });
      }
    }
    void hideOnChatKeyboard();
    return () => {
      async function cleanListeners() {
        if (showListener) {
          await showListener.remove();
        }
        if (hideListener) {
          await hideListener.remove();
        }
      }
      void cleanListeners();
    };
  }, []);

  return {
    actions: {
      handleHaptic,
      setOpen,
    },
    state: {
      activeOrg: org,
      activeProjectId,
      groupProjectList,
      keyboardOpen,
      open,
      orgSettings,
      originalInviter,
      projects,
      setOpen,
      showSettingsDrawer: !keyboardOpen && breakpoint === "mobile",
      user: userInfos, // This should never be null
    },
  };
});

// TODO: Properly refactor to have only one single org
// Remove dropdown, remove notifications aggregation logic, ...
function NavigationDrawer(props: { children: React.ReactNode }) {
  const { actions, state } = useNavigationDrawer();

  if (!state.user) {
    console.info(
      "NavigationDrawer: user details are not ready; rendering content without the drawer"
    );
    return (
      <Drawer open={false} setOpen={actions.setOpen}>
        {props.children}
      </Drawer>
    );
  }

  return (
    <Drawer open={state.open} setOpen={actions.setOpen}>
      <DrawerContent
        position="left"
        safeAreaTop={false}
        safeAreaBottom={false}
        background={false}
        css={{
          // helps to display the close arrow
          overflow: "visible",
        }}
      >
        <Box
          hoverEffect
          display="flex"
          position={"absolute"}
          flexDirection="column"
          justifyContent="center"
          onClick={() => state.setOpen(false)}
          css={{
            cursor: "pointer",
            opacity: 0.9,
            right: "-15px",
            // centering the button vertically
            top: "calc(50% - 25px)",
            zIndex: 1,
          }}
        >
          <Stack align="right">
            <Button
              variant="solid"
              size="small"
              circular={true}
              dataClovisActionId="close-projects-menu"
            >
              <ChevronLeftIcon />
            </Button>
          </Stack>
        </Box>
        <Box
          display="grid"
          css={{
            gridTemplate: "1fr auto / 1fr",
            height: "100%",
            maxWidth: "360px",
            width: "calc(100vw - 20px)",
          }}
        >
          <Box
            overflowY="auto"
            width="full"
            background="navyDark"
            display="flex"
            flexDirection="column"
            css={{
              overflowY: "auto", // This enables vertical scrolling
            }}
          >
            <NavigationDrawerHeader
              activeOrg={state.activeOrg}
              originalInviter={state.originalInviter}
              keyboardOpen={state.keyboardOpen}
            />
            {state.projects && (
              <ProjectListMenu
                closeDrawer={() => actions.setOpen(false)}
                projects={state.projects}
                activeProjectId={state.activeProjectId}
                groupProjectList={state.groupProjectList}
                orgSettings={state.orgSettings}
              />
            )}
          </Box>
          {state.showSettingsDrawer && (
            <SettingsDrawer
              closeDrawer={() => actions.setOpen(false)}
              orgs={state.activeOrg ? [state.activeOrg] : []}
              activeOrgId={state.user.active_org?.org.id}
            >
              <DrawerTrigger asChild>
                <Box width="full" onClick={actions.handleHaptic}>
                  <SettingsDrawerButton user={state.user} />
                </Box>
              </DrawerTrigger>
            </SettingsDrawer>
          )}
        </Box>
      </DrawerContent>
      {props.children}
    </Drawer>
  );
}

export type { GroupProjectList, OrgSettings, UserInfos, UserOrg, UserProject };
export { NavigationDrawer, useNavigationDrawer };
